import { memo, useState, useMemo } from "react";
import {
  ArrowLeft,
  Copy,
  Trophy,
  Users,
  QrCode,
  Loader2,
  TrendingUp,
  Check,
  Info,
  LogIn,
} from "lucide-react";
import { Button } from "./Button";
import { Badge } from "./Badge";
// import { BannerAd } from "./BannerAd";
import { UserMetrics } from "./UserMetrics";
import { useLanguage } from "../contexts/LanguageContext";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";
import { StoryModal } from "./StoryModal";
import { CreateSquadModal } from "./CreateSquadModal";
import { JoinSquadModal } from "./JoinSquadModal";
import { AchievementsModal } from "./AchievementsModal";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { useAchievements } from "../hooks/useAchievements";
import { usePredictions } from "../hooks/usePredictions";
import { useAuth } from "../contexts/AuthContext";
import { useSquad } from "../contexts/SquadContext";
import { formatTimeAgo } from "../lib/dateUtils";

interface LeaderboardScreenProps {
  onBack: () => void;
  onNavigate?: (screen: "landing" | "leaderboard") => void;
}

const BADGES = ["👑", "🔥", "⚡", "🎯", "🌟", "💎", "🎖️", "🏅", "⭐", "✨"];

export const LeaderboardScreen = memo(function LeaderboardScreen({
  onBack,
}: LeaderboardScreenProps) {
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);
  const [showJoinSquadModal, setShowJoinSquadModal] = useState(false);
  const [showAchievementsModal, setShowAchievementsModal] = useState(false);
  const { t, language } = useLanguage();
  const { getCountryName } = useCountryTranslations();
  const { user } = useAuth();
  const { mode, currentSquad } = useSquad();
  const { leaderboard, loading } = useLeaderboard(100);
  const { achievements, loading: achievementsLoading } = useAchievements(
    user?.id
  );
  const { predictions: recentPredictions, loading: predictionsLoading } =
    usePredictions(
      undefined,
      undefined,
      mode === "squad" && currentSquad ? currentSquad.id : undefined
    );

  // Generate invite link based on current league mode
  const inviteLink = useMemo(() => {
    if (mode === "squad" && currentSquad) {
      return `https://afconpredict.com/squad/${currentSquad.code}`;
    }
    return "https://afconpredict.com";
  }, [mode, currentSquad]);

  const topThree = useMemo(() => leaderboard.slice(0, 3), [leaderboard]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback method
        const input = document.createElement("input");
        input.value = inviteLink;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      // If copy fails, just show the copied state briefly to indicate the URL is available
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyCode = async () => {
    if (!currentSquad?.code) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(currentSquad.code);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } else {
        // Fallback method
        const input = document.createElement("input");
        input.value = currentSquad.code;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      }
    } catch (error) {
      // If copy fails, just show the copied state briefly
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 gradient-mesh">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
          >
            <ArrowLeft
              className={`w-5 h-5 text-slate-600 ${
                language === "ar" ? "rotate-180" : ""
              }`}
            />
          </button>
          <span className="text-slate-900 font-semibold">
            {t("leaderboard.title")}
          </span>
          <div className="w-9 h-9" />
        </div>
      </div>

      <main className="max-w-md mx-auto w-full px-4 py-6">
        {/* League Info */}
        <div className="gradient-primary rounded-3xl p-7 mb-6 text-white shadow-xl relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-yellow-400 p-3 rounded-2xl shadow-lg">
                <Trophy className="w-8 h-8 text-primary-dark" />
              </div>
              <div className="flex-1">
                <h1 className="mb-1">
                  {mode === "squad" && currentSquad
                    ? currentSquad.name
                    : t("leaderboard.globalLeague")}
                </h1>
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <Users className="w-4 h-4" />
                  <span>
                    {leaderboard.length} {t("leaderboard.players")}
                  </span>
                  {mode === "squad" && currentSquad && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-semibold">
                          {currentSquad.code}
                        </span>
                        <button
                          onClick={handleCopyCode}
                          className="p-1 hover:bg-white/20 rounded transition-smooth"
                          aria-label={
                            copiedCode
                              ? t("leaderboard.copied")
                              : t("leaderboard.copy")
                          }
                          title={
                            copiedCode
                              ? t("leaderboard.copied")
                              : t("leaderboard.copy")
                          }
                        >
                          {copiedCode ? (
                            <Check className="w-3 h-3 text-white" />
                          ) : (
                            <Copy className="w-3 h-3 text-white/70" />
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-95 font-semibold">
                  {t("leaderboard.inviteFriends")}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowStoryModal(true)}
                    className="px-3 py-2 bg-white/15 rounded-xl hover:bg-white/25 transition-smooth flex items-center gap-2 border border-white/20 active:scale-95"
                  >
                    <QrCode className="w-4 h-4" />
                    <span className="text-xs font-semibold">
                      {t("leaderboard.story")}
                    </span>
                  </button>
                  <button
                    onClick={handleCopy}
                    className="px-3 py-2 bg-white/15 rounded-xl hover:bg-white/25 transition-smooth flex items-center gap-2 border border-white/20 active:scale-95"
                  >
                    <Copy className="w-4 h-4" />
                    <span className="text-xs font-semibold">
                      {copied ? t("leaderboard.copied") : t("leaderboard.copy")}
                    </span>
                  </button>
                </div>
              </div>
              <div className="text-xs bg-white/10 p-3 rounded-xl break-all border border-white/10 font-mono">
                {inviteLink}
              </div>
            </div>
          </div>
        </div>

        {/* Create/Join League CTA - Only show if user hasn't joined a league */}
        {!currentSquad && (
          <div className="flex flex-col gap-3 mb-6">
            <Button
              variant="primary"
              fullWidth
              onClick={() => setShowJoinSquadModal(true)}
            >
              <div className="flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" />
                <span>{t("league.join")}</span>
              </div>
            </Button>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setShowCreateSquadModal(true)}
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="w-5 h-5" />
                <span>{t("leaderboard.createLeague")}</span>
              </div>
            </Button>
          </div>
        )}

        {/* User Metrics */}
        <div className="mb-6">
          <UserMetrics variant="card" />
        </div>

        {/* Banner Ad - Between Metrics and Achievements */}
        {/* <div className="mb-6">
          <BannerAd size="small" variant="subtle" />
        </div> */}

        {/* Achievement Badges */}
        <div className="bg-white rounded-3xl shadow-md p-6 mb-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm text-slate-900 font-semibold uppercase tracking-wide">
              {t("leaderboard.achievements")}
            </h3>
            <button
              onClick={() => setShowAchievementsModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00864a]/10 hover:bg-[#00864a]/20 text-[#00864a] transition-colors"
            >
              <Info className="w-4 h-4" />
              <span className="text-xs">{t("leaderboard.badgeGuide")}</span>
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {achievementsLoading ? (
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            ) : achievements.length === 0 ? (
              <p className="text-sm text-slate-500">{t("common.noPlayers")}</p>
            ) : (
              achievements.map((achievement) => (
                <Badge
                  key={achievement.key}
                  emoji={achievement.earned ? achievement.emoji : "🔒"}
                  label={
                    achievement.earned
                      ? t(achievement.labelKey)
                      : `${achievement.progress}/${achievement.target}`
                  }
                  variant={achievement.earned ? "gold" : "default"}
                />
              ))
            )}
          </div>
        </div>

        {/* Top 3 Podium - Always visible */}
        {loading ? (
          <div className="flex items-center justify-center py-12 mb-6">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : (
          <div className="mb-6">
            <div className="flex items-end justify-center gap-2.5 mb-4">
              {/* 2nd Place */}
              <div
                className={`flex-1 text-center ${
                  !topThree[1] || topThree[1].total_points === 0
                    ? "opacity-30"
                    : ""
                }`}
              >
                <div className="bg-gradient-to-br from-gray-400 to-gray-500 rounded-2xl p-4 mb-2 shadow-lg">
                  <div className="text-4xl mb-2">🥈</div>
                  {topThree[1] && topThree[1].total_points > 0 ? (
                    <>
                      <div className="text-xs text-white font-semibold">
                        {topThree[1].name}
                      </div>
                      <div className="text-lg text-white mt-1 font-bold">
                        {topThree[1].total_points}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-white font-semibold mt-1">
                      -
                    </div>
                  )}
                </div>
                <div className="bg-gradient-to-b from-gray-400 to-gray-500 h-16 rounded-t-2xl"></div>
              </div>

              {/* 1st Place */}
              <div
                className={`flex-1 text-center ${
                  !topThree[0] || topThree[0].total_points === 0
                    ? "opacity-30"
                    : ""
                }`}
              >
                <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl p-5 mb-2 shadow-xl">
                  <div className="text-5xl mb-2">👑</div>
                  {topThree[0] && topThree[0].total_points > 0 ? (
                    <>
                      <div className="text-xs text-primary-dark font-semibold">
                        {topThree[0].name}
                      </div>
                      <div className="text-xl text-primary-dark mt-1 font-bold">
                        {topThree[0].total_points}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-primary-dark font-semibold mt-1">
                      -
                    </div>
                  )}
                </div>
                <div className="bg-gradient-to-b from-yellow-400 to-yellow-500 h-24 rounded-t-2xl"></div>
              </div>

              {/* 3rd Place */}
              <div
                className={`flex-1 text-center ${
                  !topThree[2] || topThree[2].total_points === 0
                    ? "opacity-30"
                    : ""
                }`}
              >
                <div className="bg-amber-700 rounded-xl p-4 mb-2 shadow-sm">
                  <div className="text-4xl mb-2">🥉</div>
                  {topThree[2] && topThree[2].total_points > 0 ? (
                    <>
                      <div className="text-xs text-white font-medium">
                        {topThree[2].name}
                      </div>
                      <div className="text-lg text-white mt-1 font-semibold">
                        {topThree[2].total_points}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-white font-medium mt-1">-</div>
                  )}
                </div>
                <div className="bg-amber-700 h-12 rounded-t-xl"></div>
              </div>
            </div>
          </div>
        )}

        {/* Full Leaderboard */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6 border border-gray-100">
          <h2 className="mb-4 text-gray-800">{t("leaderboard.topPlayers")}</h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>{t("common.noPlayers")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((player) => (
                <div
                  key={player.user_id}
                  className={`flex items-center justify-between p-3 rounded-xl transition-all 
                  ${
                    player.rank <= 3 && player.total_points > 0
                      ? "bg-primary-light border border-primary/20"
                      : "hover:bg-gray-50"
                  }
                  `}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {player.total_points > 0 ? (
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold 
                        ${player.rank === 1 ? "bg-yellow-400 text-primary" : ""}
                        ${player.rank === 2 ? "bg-gray-300 text-gray-800" : ""}
                        ${player.rank === 3 ? "bg-amber-700 text-white" : ""}
                        ${player.rank > 3 ? "bg-gray-100 text-gray-600" : ""}
                      `}
                      >
                        {player.rank}
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-medium text-gray-400 text-sm">
                        -
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {player.name}
                        </span>
                        <span>{BADGES[(player.rank - 1) % BADGES.length]}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span>
                          {player.total_predictions}{" "}
                          {t("leaderboard.predictions")}
                        </span>
                        <span>•</span>
                        <span>
                          {player.accuracy.toFixed(0)}%{" "}
                          {t("leaderboard.accuracy")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-primary font-semibold">
                          {player.total_points}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t("leaderboard.points")}
                        </div>
                      </div>
                      {player.trend === "up" && (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      )}
                      {player.trend === "down" && (
                        <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Predictions Feed */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6 border border-gray-100">
          <h3 className="text-sm text-slate-900 mb-4 font-semibold uppercase tracking-wide">
            {t("leaderboard.recent")}
          </h3>
          {predictionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            </div>
          ) : recentPredictions.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              {t("common.noPredictions")}
            </div>
          ) : (
            <div className="space-y-3">
              {recentPredictions.slice(0, 5).map((pred, idx) => {
                const moodEmoji =
                  pred.mood === "confident"
                    ? "😎"
                    : pred.mood === "nervous"
                    ? "😰"
                    : "😈";
                const homeTeamName = getCountryName(pred.homeTeam);
                const awayTeamName = getCountryName(pred.awayTeam);

                return (
                  <div
                    key={idx}
                    className="flex items-center gap-3 pb-3 border-b border-slate-200 last:border-0"
                  >
                    <div className="text-2xl bg-slate-50 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      {moodEmoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">
                        {pred.userName || "Anonymous"}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {homeTeamName} {pred.homeScore}-{pred.awayScore}{" "}
                        {awayTeamName}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {pred.createdAt ? formatTimeAgo(pred.createdAt, t) : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Story Modal */}
      <StoryModal
        isOpen={showStoryModal}
        onClose={() => setShowStoryModal(false)}
        inviteLink={inviteLink}
        squadName={mode === "squad" && currentSquad ? currentSquad.name : undefined}
      />

      {/* Create League Modal */}
      <CreateSquadModal
        isOpen={showCreateSquadModal}
        onClose={() => setShowCreateSquadModal(false)}
        onSuccess={() => {
          setShowCreateSquadModal(false);
          // Optionally show success message or update UI
        }}
      />

      {/* Join Squad Modal */}
      <JoinSquadModal
        isOpen={showJoinSquadModal}
        onClose={() => setShowJoinSquadModal(false)}
      />

      {/* Achievements Modal */}
      <AchievementsModal
        isOpen={showAchievementsModal}
        onClose={() => setShowAchievementsModal(false)}
      />
    </div>
  );
});
